import usePromos from "src/hooks/http/usePromos";
import { GetProductResponse } from "src/models/product";
import { dateType, currencyType } from "src/utils/dataGridFormat";
import { getArchiveTypeLabel } from "src/utils/archiveTypeMap";
import DataGrid from "./DataGrid";
import { GridColDef } from "@mui/x-data-grid-pro";
import {useCountry } from "src/components/Context/CountryContext";
import { getConsts } from "src/utils/consts";
import moment from "moment-timezone";
import PromoStatusBadge from "src/components/Product/PromoStatusBadge";

type PromotionTabProps = {
  product: GetProductResponse;
};

const PromotionTab: React.FC<PromotionTabProps> = ({ product }: PromotionTabProps) => {
  const { isLoading, data } = usePromos(product.sku);
  const { timezone } = useCountry();
  const consts = getConsts();


  const promoColumns: GridColDef[] = [
    { field: "id", headerName: "Id" },
    { field: "description", headerName: "Description", flex: 1 },
    { 
      field: "status", 
      headerName: "Status",
      renderCell: ({ value }) => <PromoStatusBadge status={value} />,
    },
    { field: "reason", headerName: "Reason", flex: 1 },
    { 
      field: "dateFrom", 
      headerName: "From", 
      valueFormatter: (params) => 
        params.value 
          ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY") 
          : ""
    },
    { 
      field: "dateTo", 
      headerName: "To", 
      valueFormatter: (params) => 
        params.value 
          ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY") 
          : ""
    },
    { field: "price", headerName: "Price", ...currencyType },
  ];

  const getPromoStatus = (dateFrom: any, dateTo: any) => {
    const now = moment();
    const from = moment(dateFrom);
    const to = moment(dateTo);

    if (now.isBefore(from)) {
      return 'Future';
    } else if (now.isBetween(from, to, null, '[]')) {
      return 'Active';
    } else {
      return 'Expired';
    }
  };

  // Sort promos by dateFrom in descending order and add status and reason
  const sortedData = data?.slice().map((promo) => {
    const status = getPromoStatus(promo.dateFrom, promo.dateTo);
    return {
      ...promo,
      status,
      reason: status === 'Expired' ? getArchiveTypeLabel(promo.archiveType) : '',
    };
  }).sort((a, b) => {
    if (moment(a.dateFrom) === null) return 1;
    if (moment(b.dateFrom) === null) return -1;
    return moment(b.dateFrom).diff(moment(a.dateFrom))
  });

  return (
    <DataGrid
      isLoading={isLoading}
      dataGridId={consts.dataGridIds.promotionsDataGridId}
      defaultPageSize={100}
      marginTop={4}
      columns={promoColumns}
      rows={sortedData}
      hideFooter
    />
  );
};

export default PromotionTab;
